import type { ArdoqId } from '@ardoq/api-types';
import {
  isNotFoundLocation,
  isUnmatchedLocation,
  type LocationIn,
  type MatchedLocationIn,
  type NotFoundLocation,
  type UnmatchedLocation,
} from 'router/declarative/location';
import type {
  AssertValid,
  RouteTreeIsValid,
} from 'router/declarative/routeValidation';
import type { RouteNode } from 'router/declarative/routeNode';
import { presentationParamDeclarations } from 'router/presentation/presentationParamDeclarations';
import type { LoadPresentationPayload } from './actions';

export const STANDARD_PRESENTATION_ROUTE =
  '/:organizationLabel/:presentationId';
export const STANDARD_PRESENTATION_SLIDE_ROUTE =
  '/:organizationLabel/:presentationId/slide/:selectedSlide';
export const STANDARD_ORGLESS_PRESENTATION_SLIDE_ROUTE =
  '/:organizationLabel/slide/:selectedSlide';
export const CUSTOM_DOMAIN_PRESENTATION_ROUTE = '/:presentationId';
export const CUSTOM_DOMAIN_PRESENTATION_SLIDE_ROUTE =
  '/:presentationId/slide/:selectedSlide';

export const standardDomainPresentationRoutes = {
  path: { layout: true },
  searchParams: { context: presentationParamDeclarations.context },
  children: [
    {
      path: '/:organizationLabel',
      children: [
        {
          path: '/slide',
          children: [{ path: '/:selectedSlide' }],
        },
        {
          path: '/:presentationId',
          children: [
            {
              path: '/slide',
              children: [{ path: '/:selectedSlide' }],
            },
          ],
        },
      ],
    },
  ],
} as const satisfies RouteNode;

export const customDomainPresentationRoutes = {
  path: { layout: true },
  searchParams: { context: presentationParamDeclarations.context },
  children: [
    {
      path: '/:presentationId',
      children: [
        {
          path: '/slide',
          children: [{ path: '/:selectedSlide' }],
        },
        {
          path: '/:presentationId',
          redirect: { route: CUSTOM_DOMAIN_PRESENTATION_ROUTE },
          children: [
            {
              path: '/slide',
              children: [
                {
                  path: '/:selectedSlide',
                  redirect: {
                    route: CUSTOM_DOMAIN_PRESENTATION_SLIDE_ROUTE,
                  },
                },
              ],
            },
          ],
        },
      ],
    },
  ],
} as const satisfies RouteNode;

export type StandardDomainPresentationRouteTree = AssertValid<
  typeof standardDomainPresentationRoutes,
  RouteTreeIsValid<typeof standardDomainPresentationRoutes>
>;

export type CustomDomainPresentationRouteTree = AssertValid<
  typeof customDomainPresentationRoutes,
  RouteTreeIsValid<typeof customDomainPresentationRoutes>
>;

export type PresentationRouteTree =
  | StandardDomainPresentationRouteTree
  | CustomDomainPresentationRouteTree;

export type CustomDomainPresentationRouteState = {
  isCustomDomain: true;
  organizationLabel: null;
  presentationId: ArdoqId;
  selectedSlideId: ArdoqId | null;
  contextComponentId: ArdoqId | null;
};

export type StandardDomainPresentationRouteState =
  | {
      isCustomDomain: false;
      organizationLabel: string;
      presentationId: ArdoqId;
      selectedSlideId: ArdoqId | null;
      contextComponentId: ArdoqId | null;
    }
  | {
      isCustomDomain: false;
      organizationLabel: null;
      presentationId: ArdoqId;
      selectedSlideId: ArdoqId;
      contextComponentId: ArdoqId | null;
    };

export type PresentationRouteState =
  | CustomDomainPresentationRouteState
  | StandardDomainPresentationRouteState;

export type StandardDomainPresentationLocation =
  LocationIn<StandardDomainPresentationRouteTree>;
export type CustomDomainPresentationLocation =
  LocationIn<CustomDomainPresentationRouteTree>;
export type PresentationLocation =
  | StandardDomainPresentationLocation
  | CustomDomainPresentationLocation;

type MatchedPresentationLocation = Extract<
  MatchedLocationIn<PresentationRouteTree>,
  {
    route:
      | typeof STANDARD_PRESENTATION_ROUTE
      | typeof STANDARD_PRESENTATION_SLIDE_ROUTE
      | typeof STANDARD_ORGLESS_PRESENTATION_SLIDE_ROUTE
      | typeof CUSTOM_DOMAIN_PRESENTATION_ROUTE
      | typeof CUSTOM_DOMAIN_PRESENTATION_SLIDE_ROUTE;
  }
>;

export function getPresentationRouteTree(
  isCustomDomain: true
): CustomDomainPresentationRouteTree;
export function getPresentationRouteTree(
  isCustomDomain: false
): StandardDomainPresentationRouteTree;
export function getPresentationRouteTree(
  isCustomDomain: boolean
): PresentationRouteTree {
  if (isCustomDomain) return customDomainPresentationRoutes;
  return standardDomainPresentationRoutes;
}

export const invalidPresentationRouteError = (): Error =>
  new Error('Invalid presentation route');

const isPresentationRoute = (route: string): boolean =>
  route === STANDARD_PRESENTATION_ROUTE ||
  route === STANDARD_PRESENTATION_SLIDE_ROUTE ||
  route === STANDARD_ORGLESS_PRESENTATION_SLIDE_ROUTE ||
  route === CUSTOM_DOMAIN_PRESENTATION_ROUTE ||
  route === CUSTOM_DOMAIN_PRESENTATION_SLIDE_ROUTE;

const readPresentationId = (location: MatchedPresentationLocation): ArdoqId => {
  if (location.route === STANDARD_ORGLESS_PRESENTATION_SLIDE_ROUTE) {
    return location.params.organizationLabel;
  }
  return location.params.presentationId;
};

export const readOrganizationLabel = (
  location: MatchedPresentationLocation
): string | null => {
  if (
    location.route === STANDARD_PRESENTATION_ROUTE ||
    location.route === STANDARD_PRESENTATION_SLIDE_ROUTE
  ) {
    return location.params.organizationLabel;
  }
  return null;
};

const readSelectedSlideId = (
  location: MatchedPresentationLocation
): ArdoqId | null => {
  if (
    location.route === STANDARD_PRESENTATION_SLIDE_ROUTE ||
    location.route === STANDARD_ORGLESS_PRESENTATION_SLIDE_ROUTE ||
    location.route === CUSTOM_DOMAIN_PRESENTATION_SLIDE_ROUTE
  ) {
    return location.params.selectedSlide;
  }
  return null;
};

const readContextComponentId = (
  location: MatchedPresentationLocation
): ArdoqId | null => location.searchParams?.context ?? null;

export const toLoadPresentationPayload = (
  location: MatchedPresentationLocation
): LoadPresentationPayload => {
  const isOrglessStandardSlide =
    location.route === STANDARD_ORGLESS_PRESENTATION_SLIDE_ROUTE;
  const isCustomDomain =
    location.route === CUSTOM_DOMAIN_PRESENTATION_ROUTE ||
    location.route === CUSTOM_DOMAIN_PRESENTATION_SLIDE_ROUTE;
  const presentationId = readPresentationId(location);
  const organizationLabel = readOrganizationLabel(location);

  if (
    !presentationId ||
    (!isCustomDomain && !organizationLabel && !isOrglessStandardSlide)
  ) {
    throw invalidPresentationRouteError();
  }

  return {
    presentationId,
    organizationLabel: isCustomDomain ? null : organizationLabel,
    selectedSlideId: readSelectedSlideId(location),
    contextComponentId: readContextComponentId(location),
    isCustomDomain,
  };
};

export function toPresentationLocation(
  state: CustomDomainPresentationRouteState
): CustomDomainPresentationLocation;
export function toPresentationLocation(
  state: StandardDomainPresentationRouteState
): StandardDomainPresentationLocation;
export function toPresentationLocation(
  state: PresentationRouteState
): PresentationLocation;
export function toPresentationLocation(
  state: PresentationRouteState
): PresentationLocation {
  const searchParams = state.contextComponentId
    ? { context: state.contextComponentId }
    : undefined;

  if (state.isCustomDomain) {
    if (state.selectedSlideId !== null) {
      return {
        route: CUSTOM_DOMAIN_PRESENTATION_SLIDE_ROUTE,
        params: {
          presentationId: state.presentationId,
          selectedSlide: state.selectedSlideId,
        },
        searchParams,
      };
    }

    return {
      route: CUSTOM_DOMAIN_PRESENTATION_ROUTE,
      params: { presentationId: state.presentationId },
      searchParams,
    };
  }

  if (!state.organizationLabel) {
    if (state.selectedSlideId === null) {
      throw invalidPresentationRouteError();
    }

    return {
      route: STANDARD_ORGLESS_PRESENTATION_SLIDE_ROUTE,
      params: {
        organizationLabel: state.presentationId,
        selectedSlide: state.selectedSlideId,
      },
      searchParams,
    };
  }

  if (state.selectedSlideId !== null) {
    return {
      route: STANDARD_PRESENTATION_SLIDE_ROUTE,
      params: {
        organizationLabel: state.organizationLabel,
        presentationId: state.presentationId,
        selectedSlide: state.selectedSlideId,
      },
      searchParams,
    };
  }

  return {
    route: STANDARD_PRESENTATION_ROUTE,
    params: {
      organizationLabel: state.organizationLabel,
      presentationId: state.presentationId,
    },
    searchParams,
  };
}

export const isMatchedPresentationLocation = (
  location:
    | MatchedLocationIn<PresentationRouteTree>
    | NotFoundLocation
    | UnmatchedLocation
    | false
): location is MatchedPresentationLocation =>
  location !== false &&
  !isNotFoundLocation(location) &&
  !isUnmatchedLocation(location) &&
  isPresentationRoute(location.route) &&
  (('presentationId' in location.params &&
    Boolean(location.params.presentationId)) ||
    (location.route === STANDARD_ORGLESS_PRESENTATION_SLIDE_ROUTE &&
      'organizationLabel' in location.params &&
      Boolean(location.params.organizationLabel)));
